import { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { addDays, format, parseISO, isSameDay } from "date-fns";
import { PermissionGate } from "@/components/PermissionGate";
import { useAppData } from "@/data/AppDataContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import WarehouseInventoryPage from "./WarehouseInventoryPage";
import { PaginationBar } from "@/components/PaginationBar";
import type { Job, MaterialReadiness } from "@/data/types";
import { isExternalManagedImport } from "@/data/import/migrationVisibilityGuard";
import { usePagination } from "@/hooks/usePagination";
import { useCountedServerPagination } from "@/hooks/useServerPagination";
import { WAREHOUSE_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import {
  useWarehouseHubJobsPage,
  useWarehouseHubLoadoutPage,
  useWarehouseHubStagingPage,
  useWarehouseHubSummary,
  useWarehouseRealtimeRefreshSignal,
} from "@/hooks/useWarehouseServerPaging";
import { WarehousePageService, type WarehouseHubLoadoutRow, type WarehouseHubStagingRow, type WarehouseHubJobRow } from "@/services/warehouse-page.service";
import { toast } from "sonner";

/* ── Quiet badge: only exceptional states carry color. Healthy/normal states render as muted text. ── */
const QUIET_TONE: Record<string, "critical" | "attention" | "muted"> = {
  "Missing Items": "critical",
  "Field Purchase Required": "critical",
  "Not Ordered": "critical",
  "Issue": "critical",
  "Permit and Order Pending": "attention",
  "QA Review": "attention",
  "Partially Received": "attention",
  "Revision Needed": "attention",
};
function QuietBadge({ value }: { value: string }) {
  const tone = QUIET_TONE[value] ?? "muted";
  if (tone === "muted") {
    return <span className="text-xs text-muted-foreground tabular-nums">{value}</span>;
  }
  const cls = tone === "critical" ? "text-destructive" : "text-warning";
  return <span className={cn("text-xs font-medium", cls)}>{value}</span>;
}
import {
  CheckCircle, ArrowRight,
  PackageX, FileWarning, ShieldAlert, Search,
} from "lucide-react";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { CockpitTabs, CockpitKpiStrip } from "@/components/cockpit/CockpitShell";
import { cn } from "@/lib/utils";

/* ── Progress calculation from materialReadiness ── */
const EXTERNAL_READINESS_LABEL = "Externally Managed" as const;
type WarehouseReadinessDisplay = MaterialReadiness | typeof EXTERNAL_READINESS_LABEL;
type WarehouseJob = Job | WarehouseHubJobRow;
const MATERIAL_GATE_PASSED_READINESS = new Set<MaterialReadiness>([
  "Fully Received",
  "Staged for Job",
  "Loaded for Crew",
]);

function normalizeReadiness(value: unknown): MaterialReadiness {
  return String(value ?? "Not Ordered").trim() as MaterialReadiness;
}

function isMaterialGatePassed(readiness: unknown): boolean {
  return MATERIAL_GATE_PASSED_READINESS.has(normalizeReadiness(readiness));
}

function getTerminalMaterialReadinessSignal(job: WarehouseJob): MaterialReadiness | null {
  const metadata = (job as { metadata?: Record<string, unknown> }).metadata ?? {};
  const candidates = [
    job.materialReadiness,
    job.materialsStatus,
    job.procurementStatus,
    (job as { jobMaterialStatus?: unknown }).jobMaterialStatus,
    (job as { warehouseStatus?: unknown }).warehouseStatus,
    (job as { planningStatus?: unknown }).planningStatus,
    metadata.materialReadiness,
    metadata.materialsStatus,
    metadata.jobMaterialStatus,
    metadata.warehouseStatus,
    metadata.planningStatus,
  ];

  for (const candidate of candidates) {
    const value = String(candidate ?? "").trim();
    if (MATERIAL_GATE_PASSED_READINESS.has(value as MaterialReadiness)) {
      return value as MaterialReadiness;
    }
    if (value === "Ready for Install" || value === "Received") return "Fully Received";
    if (value === "Staged") return "Staged for Job";
    if (value === "Loaded") return "Loaded for Crew";
    if (value === "Ready" || value === "Confirmed") return "Fully Received";
  }

  return null;
}

const READINESS_PROGRESS: Record<string, number> = {
  "Not Ordered": 0,
  "Missing Items": 0,
  "Field Purchase Required": 5,
  "Ordered": 25,
  "Partially Received": 38,
  "Fully Received": 50,
  "Staged for Job": 75,
  "Loaded for Crew": 100,
  [EXTERNAL_READINESS_LABEL]: 100,
};

function getJobDisplayRef(job: { id: string; jobNumber?: unknown }): string {
  const jobNumber = typeof job.jobNumber === "string" ? job.jobNumber.trim() : "";
  return jobNumber || job.id;
}

function getMaterialProgress(readiness: WarehouseReadinessDisplay): number {
  return READINESS_PROGRESS[readiness] ?? 0;
}

function getProgressColor(progress: number, readiness: WarehouseReadinessDisplay): string {
  if (readiness === "Missing Items" || readiness === "Not Ordered") return "bg-destructive";
  if (progress >= 100) return "bg-success";
  if (progress >= 50) return "bg-primary";
  return "bg-warning";
}

function getNumberField(row: unknown, primary: string, legacy?: string): number {
  const rec = row as Record<string, unknown>;
  const raw = rec[primary] ?? (legacy ? rec[legacy] : undefined) ?? 0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
}

function getMaterialIssueNames(job: WarehouseJob): string {
  if (getTerminalMaterialReadinessSignal(job)) return "";

  const materials = Array.isArray(job.materialsList) ? job.materialsList : [];
  return materials
    .filter((material) => getNumberField(material, "missingItems") > 0 || Boolean(material.fieldPurchase))
    .map((material) => String(material.itemName ?? material.sku ?? "").trim())
    .filter(Boolean)
    .join(", ");
}

function hasMissingMaterialEvidence(job: WarehouseJob): boolean {
  if (getTerminalMaterialReadinessSignal(job)) return false;
  return Boolean(String(job.missingItemsList ?? "").trim() || getMaterialIssueNames(job));
}

function hasZeroProcurementNote(job: WarehouseJob): boolean {
  return /\b0\s+items?\s+need\s+procurement\b/i.test(String(job.materialReadinessNotes ?? ""));
}

function deriveReadinessFromMaterialLines(job: WarehouseJob): MaterialReadiness | null {
  if (getTerminalMaterialReadinessSignal(job)) return null;

  const materials = Array.isArray(job.materialsList) ? job.materialsList : [];
  const realRows = materials.filter((material) => {
    const required = getNumberField(material, "quantityRequired");
    const label = String(material.itemName ?? material.sku ?? "").trim();
    return required > 0 && label.length > 0;
  });

  if (realRows.length === 0) return null;

  const totalRequired = realRows.reduce((sum, material) => sum + getNumberField(material, "quantityRequired"), 0);
  const totalAllocated = realRows.reduce((sum, material) => sum + getNumberField(material, "quantityAllocated", "allocatedQuantity"), 0);
  const totalStaged = realRows.reduce((sum, material) => sum + getNumberField(material, "quantityStaged", "stagedQuantity"), 0);
  const totalInstalled = realRows.reduce((sum, material) => sum + getNumberField(material, "quantityInstalled", "installedQuantity"), 0);
  const hasMissing = realRows.some((material) => getNumberField(material, "missingItems") > 0);
  const hasFieldPurchase = realRows.some((material) => Boolean(material.fieldPurchase));

  if (hasMissing) return "Missing Items";
  if (hasFieldPurchase) return "Field Purchase Required";
  if (totalRequired > 0 && totalAllocated >= totalRequired) {
    return totalStaged > 0 || totalInstalled > 0 ? "Staged for Job" : "Fully Received";
  }

  return null;
}

function getEffectiveReadiness(job: WarehouseJob): WarehouseReadinessDisplay {
  const materialReadiness = normalizeReadiness(job.materialReadiness);
  const terminalReadiness = getTerminalMaterialReadinessSignal(job);

  if (
    isExternalManagedImport(job as Pick<Job, "sourceMeta">) &&
    ["Missing Items", "Field Purchase Required", "Not Ordered"].includes(materialReadiness)
  ) {
    return EXTERNAL_READINESS_LABEL;
  }

  if (terminalReadiness) {
    return terminalReadiness;
  }

  const derivedReadiness = deriveReadinessFromMaterialLines(job);
  if (derivedReadiness) return derivedReadiness;

  if (
    ["Missing Items", "Field Purchase Required"].includes(materialReadiness) &&
    !hasMissingMaterialEvidence(job)
  ) {
    return "Not Ordered";
  }

  return materialReadiness;
}

function isWarehouseReady(job: WarehouseJob): boolean {
  const readiness = getEffectiveReadiness(job);
  return (
    readiness === "Fully Received" ||
    readiness === "Staged for Job" ||
    readiness === "Loaded for Crew" ||
    readiness === EXTERNAL_READINESS_LABEL
  );
}

function getMissingItemsDisplay(job: WarehouseJob): string {
  const readiness = getEffectiveReadiness(job);
  if (readiness === EXTERNAL_READINESS_LABEL) {
    return "External system owns materials readiness";
  }
  if (MATERIAL_GATE_PASSED_READINESS.has(readiness)) return "";

  return job.missingItemsList || getMaterialIssueNames(job);
}

function getReadinessNotesDisplay(job: WarehouseJob): string {
  if (getTerminalMaterialReadinessSignal(job)) return "";
  if (hasZeroProcurementNote(job)) return "";
  return job.materialReadinessNotes || "";
}

/* ── Blocker detection ── */
interface BlockerInfo {
  missing: boolean;
  notOrdered: boolean;
  permitBlocked: boolean;
}

function getBlockers(job: WarehouseJob): BlockerInfo {
  const readiness = getEffectiveReadiness(job);
  return {
    missing: readiness === "Missing Items" || readiness === "Field Purchase Required",
    notOrdered: readiness === "Not Ordered",
    permitBlocked: job.permitRequired && (job.permitStatus === "Revision Needed" || job.permitStatus === "Submitted"),
  };
}

function hasAnyBlocker(b: BlockerInfo) {
  return b.missing || b.notOrdered || b.permitBlocked;
}

type WarehouseTab = "readiness" | "stock" | "staging" | "loadout" | "incoming" | "prep" | "exceptions";
const TABS: readonly WarehouseTab[] = ["readiness", "stock", "staging", "loadout", "incoming", "prep", "exceptions"];
interface PaginationControls {
  page: number;
  totalPages: number;
  pageStart: number;
  pageEnd: number;
  totalItems: number;
  hasNext: boolean;
  hasPrev: boolean;
  next: () => void;
  prev: () => void;
  pageSize: number;
  setPageSize: (size: number) => void;
}
function isTab(v: string | null): v is WarehouseTab {
  return !!v && (TABS as readonly string[]).includes(v);
}

export default function WarehouseReadiness() {
  const { jobs, stagingRecords } = useAppData();
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const warehouseServerPaging = WAREHOUSE_SERVER_PAGING_ENABLED;
  const realtimeRefreshToken = useWarehouseRealtimeRefreshSignal(warehouseServerPaging);
  const [manualRefreshToken, setManualRefreshToken] = useState(0);
  const serverRefreshToken = realtimeRefreshToken + manualRefreshToken;
  const refreshWarehouseHubPages = () => setManualRefreshToken((current) => current + 1);

  const initialTab: WarehouseTab = isTab(searchParams.get("tab")) ? (searchParams.get("tab") as WarehouseTab) : "readiness";
  const [tab, setTab] = useState<WarehouseTab>(initialTab);
  useEffect(() => { setTab(initialTab); }, [initialTab]);
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (next.get("tab") !== tab) {
      next.set("tab", tab);
      setSearchParams(next, { replace: true });
    }
  }, [tab, searchParams, setSearchParams]);

  const readinessServerControls = useCountedServerPagination(20);
  const incomingServerControls = useCountedServerPagination(15);
  const fullyReceivedServerControls = useCountedServerPagination(15);
  const stagedServerControls = useCountedServerPagination(15);
  const exceptionsServerControls = useCountedServerPagination(15);
  const prepServerControls = useCountedServerPagination(15);
  const stagingServerControls = useCountedServerPagination(10);
  const loadoutServerControls = useCountedServerPagination(10);

  const hubSummary = useWarehouseHubSummary({
    enabled: warehouseServerPaging,
    refreshToken: serverRefreshToken,
  });
  const readinessServer = useWarehouseHubJobsPage({
    enabled: warehouseServerPaging,
    lane: "all",
    page: readinessServerControls.page,
    pageSize: readinessServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const incomingServer = useWarehouseHubJobsPage({
    enabled: warehouseServerPaging,
    lane: "incoming",
    page: incomingServerControls.page,
    pageSize: incomingServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const fullyReceivedServer = useWarehouseHubJobsPage({
    enabled: warehouseServerPaging,
    lane: "fully_received",
    page: fullyReceivedServerControls.page,
    pageSize: fullyReceivedServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const stagedServer = useWarehouseHubJobsPage({
    enabled: warehouseServerPaging,
    lane: "ready",
    page: stagedServerControls.page,
    pageSize: stagedServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const exceptionsServer = useWarehouseHubJobsPage({
    enabled: warehouseServerPaging,
    lane: "exceptions",
    page: exceptionsServerControls.page,
    pageSize: exceptionsServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const prepServer = useWarehouseHubJobsPage({
    enabled: warehouseServerPaging,
    lane: "prep",
    page: prepServerControls.page,
    pageSize: prepServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const stagingServer = useWarehouseHubStagingPage({
    enabled: warehouseServerPaging,
    lane: "all",
    page: stagingServerControls.page,
    pageSize: stagingServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const loadoutServer = useWarehouseHubLoadoutPage({
    enabled: warehouseServerPaging,
    lane: "all",
    page: loadoutServerControls.page,
    pageSize: loadoutServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });

  useEffect(() => { readinessServerControls.setTotalItems(readinessServer.count); }, [readinessServer.count]);
  useEffect(() => { incomingServerControls.setTotalItems(incomingServer.count); }, [incomingServer.count]);
  useEffect(() => { fullyReceivedServerControls.setTotalItems(fullyReceivedServer.count); }, [fullyReceivedServer.count]);
  useEffect(() => { stagedServerControls.setTotalItems(stagedServer.count); }, [stagedServer.count]);
  useEffect(() => { exceptionsServerControls.setTotalItems(exceptionsServer.count); }, [exceptionsServer.count]);
  useEffect(() => { prepServerControls.setTotalItems(prepServer.count); }, [prepServer.count]);
  useEffect(() => { stagingServerControls.setTotalItems(stagingServer.count); }, [stagingServer.count]);
  useEffect(() => { loadoutServerControls.setTotalItems(loadoutServer.count); }, [loadoutServer.count]);

  const activeJobs = useMemo(() => jobs.filter((j) => j.currentStage !== "Completed"), [jobs]);

  const incoming = useMemo(() => activeJobs.filter((j) =>
    ["Ordered", "Partially Received"].includes(getEffectiveReadiness(j))
  ), [activeJobs]);

  const fullyReceived = useMemo(() => activeJobs.filter((j) => getEffectiveReadiness(j) === "Fully Received"), [activeJobs]);

  const staged = useMemo(() => activeJobs.filter((j) =>
    isWarehouseReady(j)
  ), [activeJobs]);

  const exceptions = useMemo(() => activeJobs.filter((j) =>
    ["Missing Items", "Field Purchase Required"].includes(getEffectiveReadiness(j))
  ), [activeJobs]);

  const stagingPending = stagingRecords.filter(s => s.status !== "Fully Staged").length;

  const pctReady = useMemo(() => {
    const readyCount = activeJobs.filter((j) => isWarehouseReady(j)).length;
    return activeJobs.length > 0 ? Math.round((readyCount / activeJobs.length) * 100) : 0;
  }, [activeJobs]);

  const searchFilter = useMemo(() => (list: typeof activeJobs) => {
    if (!search) return list;
    const s = search.toLowerCase();
    return list.filter((j) =>
      j.customerName.toLowerCase().includes(s) ||
      j.id.toLowerCase().includes(s) ||
      getJobDisplayRef(j).toLowerCase().includes(s) ||
      getEffectiveReadiness(j).toLowerCase().includes(s) ||
      (j.assignedCrew || "").toLowerCase().includes(s) ||
      (j.assignedSubcontractor || "").toLowerCase().includes(s) ||
      getMissingItemsDisplay(j).toLowerCase().includes(s) ||
      getReadinessNotesDisplay(j).toLowerCase().includes(s)
    );
  }, [search]);

  const filteredActiveJobs = useMemo(() => searchFilter(activeJobs), [activeJobs, searchFilter]);
  const filteredIncoming = useMemo(() => searchFilter(incoming), [incoming, searchFilter]);
  const filteredFullyReceived = useMemo(() => searchFilter(fullyReceived), [fullyReceived, searchFilter]);
  const filteredStaged = useMemo(() => searchFilter(staged), [staged, searchFilter]);
  const filteredExceptions = useMemo(() => searchFilter(exceptions), [exceptions, searchFilter]);

  const readinessPage = usePagination(filteredActiveJobs, 20);
  const stagingPage = usePagination(filteredStaged, 15);
  const stagingNeedsWorkPage = usePagination(filteredFullyReceived, 15);
  const incomingPage = usePagination(filteredIncoming, 15);
  const exceptionsPage = usePagination(filteredExceptions, 15);

  useEffect(() => {
    readinessPage.reset();
    stagingPage.reset();
    stagingNeedsWorkPage.reset();
    incomingPage.reset();
    exceptionsPage.reset();
    readinessServerControls.reset();
    incomingServerControls.reset();
    fullyReceivedServerControls.reset();
    stagedServerControls.reset();
    exceptionsServerControls.reset();
    prepServerControls.reset();
    stagingServerControls.reset();
    loadoutServerControls.reset();
  }, [search, tab]);

  const hubKpis = hubSummary.data;
  const activeJobsCount = warehouseServerPaging ? (hubKpis?.activeJobs ?? readinessServer.count) : activeJobs.length;
  const incomingCount = warehouseServerPaging ? (hubKpis?.incoming ?? incomingServer.count) : incoming.length;
  const fullyReceivedCount = warehouseServerPaging ? (hubKpis?.fullyReceived ?? fullyReceivedServer.count) : fullyReceived.length;
  const stagedCount = warehouseServerPaging ? (hubKpis?.ready ?? stagedServer.count) : staged.length;
  const exceptionsCount = warehouseServerPaging ? (hubKpis?.exceptions ?? exceptionsServer.count) : exceptions.length;
  const stagingPendingCount = warehouseServerPaging ? (hubKpis?.stagingPending ?? 0) : stagingPending;
  const prepCount = warehouseServerPaging ? (hubKpis?.prepTomorrow ?? prepServer.count) : undefined;
  const pctReadyValue = warehouseServerPaging ? (hubKpis?.pctReady ?? 0) : pctReady;

  const handleMarkStaged = async (row: WarehouseHubStagingRow) => {
    const ok = await WarehousePageService.markHubJobStaged(row);
    if (ok) {
      toast.success(`Staged ${row.jobLabel}`);
      refreshWarehouseHubPages();
    } else {
      toast.error("Could not mark job staged.");
    }
  };

  const handleMarkLoaded = async (row: WarehouseHubLoadoutRow, signedOffBy: string) => {
    const ok = await WarehousePageService.markHubJobLoaded(row.jobId, signedOffBy);
    if (ok) {
      toast.success(`Loaded ${row.jobLabel}`);
      refreshWarehouseHubPages();
    } else {
      toast.error("Could not mark load-out complete.");
    }
  };

  const tabDefs = [
    { value: "readiness" as const, label: "Readiness", count: activeJobsCount },
    { value: "stock"     as const, label: "Inventory" },
    { value: "staging"   as const, label: "Staging",   count: stagingPendingCount || undefined },
    { value: "loadout"   as const, label: "Load-Out", count: warehouseServerPaging ? (hubKpis?.loadoutPending || undefined) : undefined },
    { value: "incoming"  as const, label: "Incoming",  count: incomingCount },
    { value: "prep"      as const, label: "Daily Prep", count: prepCount },
    { value: "exceptions" as const, label: "Exceptions", count: exceptionsCount || undefined },
  ];

  const kpiStrip = (
    <CockpitKpiStrip
      items={[
        { label: "Incoming",   value: incomingCount },
        { label: "Received",   value: fullyReceivedCount },
        { label: "Staged",     value: stagedCount },
        { label: "Exceptions", value: exceptionsCount, tone: exceptionsCount > 0 ? "bad" : "default" },
        { label: "% Ready",    value: `${pctReadyValue}%`, hint: `${activeJobsCount} active jobs` },
      ]}
    />
  );

  return (
    <CockpitPage
      title="Warehouse"
      controls={
        <span className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground">
          {warehouseServerPaging ? "Warehouse · Server paged" : "Warehouse · Legacy hydration"}
        </span>
      }
      belowHeader={kpiStrip}
      tabs={
        <CockpitTabs
          tabs={tabDefs.map(t => ({ value: t.value, label: t.label, count: t.count }))}
          value={tab}
          onChange={(v) => setTab(v as WarehouseTab)}
        />
      }
    >
      <div className="mt-5 space-y-5">
        {/* Search row */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search jobs, customers, crews…"
              className="w-full h-8 pl-8 pr-3 text-xs rounded border border-border/60 bg-background placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/40"
            />
          </div>
        </div>

        {tab === "readiness" && (
          <Section title="All jobs · Material readiness" count={warehouseServerPaging ? readinessServer.count : filteredActiveJobs.length}>
            <JobTable jobs={warehouseServerPaging ? readinessServer.jobs : readinessPage.paged} showReadiness showMissing showCrew showNotes />
            <PaginationBar {...(warehouseServerPaging ? readinessServerControls : readinessPage)} />
          </Section>
        )}

        {tab === "stock" && (
          <div className="-mx-4 -mt-1">
            <WarehouseInventoryPage />
          </div>
        )}

        {tab === "staging" && (
          <>
            <ServerStagingPanel
              rows={stagingServer.rows}
              pagination={stagingServerControls}
              onMarkStaged={handleMarkStaged}
            />
            <Section title="Staged & loaded" count={stagedServer.count} hint="Materials staged for job site or loaded onto crew vehicles">
              <JobTable jobs={stagedServer.jobs} showReadiness showCrew />
              <PaginationBar {...stagedServerControls} />
            </Section>
            {fullyReceivedServer.count > 0 && (
              <Section title="Received · Needs staging" count={fullyReceivedServer.count} hint="Materials received but not yet staged or loaded">
                <JobTable jobs={fullyReceivedServer.jobs} showReadiness showCrew />
                <PaginationBar {...fullyReceivedServerControls} />
              </Section>
            )}
          </>
        )}

        {tab === "loadout" && (
          <ServerLoadOutPanel
            rows={loadoutServer.rows}
            pagination={loadoutServerControls}
            onMarkLoaded={handleMarkLoaded}
          />
        )}

        {tab === "incoming" && (
          <Section title="Incoming materials" count={warehouseServerPaging ? incomingServer.count : filteredIncoming.length}>
            <JobTable jobs={warehouseServerPaging ? incomingServer.jobs : incomingPage.paged} showReadiness showCrew />
            <PaginationBar {...(warehouseServerPaging ? incomingServerControls : incomingPage)} />
          </Section>
        )}

        {tab === "prep" && (
          <TomorrowPrepChecklist
            serverJobs={warehouseServerPaging ? prepServer.jobs : undefined}
            serverPagination={warehouseServerPaging ? prepServerControls : undefined}
          />
        )}

        {tab === "exceptions" && (
          <Section title="Missing items & exceptions" count={warehouseServerPaging ? exceptionsServer.count : filteredExceptions.length} hint="Resolve before dispatch.">
            {(warehouseServerPaging ? exceptionsServer.count : filteredExceptions.length) === 0 ? (
              <div className="py-12 text-center text-xs text-muted-foreground">
                No material exceptions — all clear.
              </div>
            ) : (
              <div className="divide-y divide-border/60 border-y border-border/60">
                {(warehouseServerPaging ? exceptionsServer.jobs : exceptionsPage.paged).map((job) => (
                  <Link key={job.id} to={`/jobs/${job.id}`} className="block hover:bg-muted/30 transition-colors">
                    <div className="flex items-center justify-between px-4 py-2.5 gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="font-mono text-[11px] text-foreground/90 shrink-0">{getJobDisplayRef(job)}</span>
                        <span className="text-xs text-foreground truncate">{job.customerName}</span>
                        <QuietBadge value={getEffectiveReadiness(job)} />
                      </div>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-[11px] text-muted-foreground max-w-[260px] truncate hidden sm:block">
                          {getMissingItemsDisplay(job) || getReadinessNotesDisplay(job) || "—"}
                        </span>
                        <ChevronRightIcon />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
            <PaginationBar {...(warehouseServerPaging ? exceptionsServerControls : exceptionsPage)} />
          </Section>
        )}
      </div>
    </CockpitPage>
  );
}

/* ── Restrained section header ── */
function Section({ title, count, hint, children }: { title: string; count?: number; hint?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-medium tracking-tight text-foreground">
          {title}
          {typeof count === "number" && (
            <span className="ml-2 text-muted-foreground/70 tabular-nums font-normal">{count}</span>
          )}
        </h2>
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function ChevronRightIcon() {
  return <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />;
}

function ServerStagingPanel({
  rows,
  pagination,
  onMarkStaged,
}: {
  rows: WarehouseHubStagingRow[];
  pagination: PaginationControls;
  onMarkStaged: (row: WarehouseHubStagingRow) => void | Promise<void>;
}) {
  if (rows.length === 0) {
    return (
      <div className="py-10 text-center text-muted-foreground text-sm">
        No staging records match current filters.
      </div>
    );
  }

  return (
    <Section title="Staging checklist" count={pagination.totalItems} hint="Derived from job material staging quantities">
      <div className="space-y-3">
        {rows.map((row) => {
          const staged = row.status === "Fully Staged";
          return (
            <div key={row.jobId} className="rounded-lg border border-border/70 bg-background">
              <div className="flex items-start justify-between gap-3 border-b border-border/60 px-4 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Link to={`/jobs/${row.jobId}`} className="font-mono text-xs font-semibold text-primary hover:underline">
                      {row.jobLabel}
                    </Link>
                    <QuietBadge value={row.status} />
                  </div>
                  <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{row.customerName}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    {row.stagedCount}/{row.itemCount} staged
                  </span>
                  {!staged && (
                    <PermissionGate permission="warehouse_edit">
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => onMarkStaged(row)}>
                        Mark staged
                      </Button>
                    </PermissionGate>
                  )}
                </div>
              </div>
              <div className="divide-y divide-border/50">
                {row.items.length === 0 ? (
                  <div className="px-4 py-3 text-xs text-muted-foreground">No material line items attached.</div>
                ) : row.items.map((item) => (
                  <div key={`${row.jobId}:${item.materialId ?? item.sku}`} className="flex items-center gap-3 px-4 py-2">
                    <Checkbox checked={staged || item.staged} disabled />
                    <div className="min-w-0 flex-1">
                      <p className={cn("truncate text-xs font-medium", (staged || item.staged) && "text-muted-foreground line-through")}>
                        {item.itemName || item.sku}
                      </p>
                      <p className="font-mono text-[10px] text-muted-foreground">{item.sku}</p>
                    </div>
                    <span className="text-[11px] text-muted-foreground tabular-nums">
                      {item.quantityStaged}/{item.quantityRequired}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <PaginationBar {...pagination} />
    </Section>
  );
}

function ServerLoadOutPanel({
  rows,
  pagination,
  onMarkLoaded,
}: {
  rows: WarehouseHubLoadoutRow[];
  pagination: PaginationControls;
  onMarkLoaded: (row: WarehouseHubLoadoutRow, signedOffBy: string) => void | Promise<void>;
}) {
  const [signOffNames, setSignOffNames] = useState<Record<string, string>>({});
  const checklistItems: Array<{ key: keyof Pick<WarehouseHubLoadoutRow, "allMaterialsLoaded" | "correctItemsVerified" | "toolsReady" | "specialInstructionsReviewed">; label: string }> = [
    { key: "allMaterialsLoaded", label: "All materials loaded on vehicle" },
    { key: "correctItemsVerified", label: "Correct items verified against list" },
    { key: "toolsReady", label: "Tools and equipment ready" },
    { key: "specialInstructionsReviewed", label: "Special instructions reviewed" },
  ];

  if (rows.length === 0) {
    return (
      <div className="py-10 text-center text-muted-foreground text-sm">
        No load-out rows match current filters.
      </div>
    );
  }

  return (
    <Section title="Load-out checklist" count={pagination.totalItems} hint="Ready jobs waiting for crew load-out">
      <div className="space-y-3">
        {rows.map((row) => {
          const completedCount = checklistItems.filter((item) => Boolean(row[item.key])).length;
          return (
            <div key={row.jobId} className={cn("rounded-lg border border-border/70 bg-background", row.complete && "border-success/30")}>
              <div className="flex items-start justify-between gap-3 border-b border-border/60 px-4 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Link to={`/jobs/${row.jobId}`} className="font-mono text-xs font-semibold text-primary hover:underline">
                      {row.jobLabel}
                    </Link>
                    <QuietBadge value={row.complete ? "Loaded for Crew" : `${completedCount}/4 checked`} />
                  </div>
                  <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{row.customerName}</p>
                </div>
                {!row.complete && (
                  <div className="flex items-center gap-2">
                    <input
                      value={signOffNames[row.jobId] ?? ""}
                      onChange={(event) => setSignOffNames((prev) => ({ ...prev, [row.jobId]: event.target.value }))}
                      placeholder="Sign-off name"
                      className="h-7 w-36 rounded border border-border/60 bg-background px-2 text-xs"
                    />
                    <PermissionGate permission="warehouse_edit">
                      <Button size="sm" className="h-7 text-[10px]" onClick={() => onMarkLoaded(row, signOffNames[row.jobId] ?? "")}>
                        Mark loaded
                      </Button>
                    </PermissionGate>
                  </div>
                )}
              </div>
              <div className="space-y-1 px-4 py-3">
                {checklistItems.map((item) => {
                  const checked = Boolean(row[item.key]);
                  return (
                    <div key={item.key} className="flex items-center gap-3 rounded border border-border/50 px-3 py-2">
                      <Checkbox checked={checked} disabled />
                      <span className={cn("text-xs", checked ? "text-muted-foreground line-through" : "font-medium")}>{item.label}</span>
                    </div>
                  );
                })}
                {row.signedOffBy && (
                  <p className="pt-2 text-[10px] text-muted-foreground">
                    Signed off by <span className="font-semibold">{row.signedOffBy}</span>
                    {row.signedOffAt ? ` on ${new Date(row.signedOffAt).toLocaleString()}` : ""}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <PaginationBar {...pagination} />
    </Section>
  );
}

/* ── Blocker Icons ── */
function BlockerIcons({ job }: { job: WarehouseJob }) {
  const b = getBlockers(job);
  if (!hasAnyBlocker(b)) return null;
  return (
    <div className="flex items-center gap-1.5">
      {b.missing && (
        <span title="Missing materials" className="text-destructive/80"><PackageX className="h-3.5 w-3.5" /></span>
      )}
      {b.notOrdered && (
        <span title="Not ordered" className="text-muted-foreground"><FileWarning className="h-3.5 w-3.5" /></span>
      )}
      {b.permitBlocked && (
        <span title="Permit issue" className="text-muted-foreground"><ShieldAlert className="h-3.5 w-3.5" /></span>
      )}
    </div>
  );
}

/* ── Progress Cell — single restrained track, neutral numerals ── */
function ProgressCell({ job }: { job: WarehouseJob }) {
  const readiness = getEffectiveReadiness(job);
  const progress = getMaterialProgress(readiness);
  const blocked = readiness === "Missing Items" || readiness === "Not Ordered" || readiness === "Field Purchase Required";
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1 rounded-full bg-muted/60 overflow-hidden">
        <div
          className={cn("h-full transition-all", blocked ? "bg-destructive/70" : "bg-foreground/70")}
          style={{ width: `${progress}%` }}
        />
      </div>
      <span className="text-[10px] font-medium tabular-nums w-9 text-right text-muted-foreground">
        {progress}%
      </span>
    </div>
  );
}

/* ── Reusable job table — hairline rows, neutral chrome ── */
function JobTable({ jobs, showReadiness, showMissing, showCrew, showNotes }: {
  jobs: WarehouseJob[];
  showReadiness?: boolean;
  showMissing?: boolean;
  showCrew?: boolean;
  showNotes?: boolean;
}) {
  if (jobs.length === 0) {
    return <p className="py-8 text-center text-xs text-muted-foreground">No jobs match current filters.</p>;
  }
  const th = "text-left py-2 px-3 font-medium text-muted-foreground/80 uppercase text-[10px] tracking-[0.08em]";
  return (
    <div className="overflow-auto border-y border-border/60">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border/60">
            <th className={th}>Job</th>
            <th className={th}>Customer</th>
            {showCrew && <th className={cn(th, "hidden sm:table-cell")}>Crew</th>}
            {showReadiness && <th className={th}>Progress</th>}
            {showReadiness && <th className={cn(th, "hidden md:table-cell")}>Status</th>}
            {showReadiness && <th className={cn(th, "hidden md:table-cell")}>Blockers</th>}
            {showMissing && <th className={cn(th, "hidden md:table-cell")}>Missing</th>}
            {showNotes && <th className={cn(th, "hidden lg:table-cell")}>Notes</th>}
            <th className={cn(th, "hidden sm:table-cell")}>Stage</th>
            <th className="py-2 px-3 w-8"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {jobs.map((job) => {
            const readiness = getEffectiveReadiness(job);
            const missingDisplay = getMissingItemsDisplay(job);
            const notesDisplay = getReadinessNotesDisplay(job);
            return (
              <tr key={job.id} className="hover:bg-muted/30 transition-colors">
                <td className="py-2 px-3 font-mono text-[11px] text-foreground/90">
                  <Link to={`/jobs/${job.id}`} className="hover:text-foreground hover:underline">{getJobDisplayRef(job)}</Link>
                </td>
                <td className="py-2 px-3 text-foreground">{job.customerName}</td>
                {showCrew && (
                  <td className="py-2 px-3 hidden sm:table-cell text-foreground/80">
                    {job.assignedCrew || job.assignedSubcontractor || <span className="text-muted-foreground">Unassigned</span>}
                  </td>
                )}
                {showReadiness && (
                  <td className="py-2 px-3"><ProgressCell job={job} /></td>
                )}
                {showReadiness && (
                  <td className="py-2 px-3 hidden md:table-cell">
                    <QuietBadge value={readiness} />
                  </td>
                )}
                {showReadiness && (
                  <td className="py-2 px-3 hidden md:table-cell">
                    <BlockerIcons job={job} />
                  </td>
                )}
                {showMissing && (
                  <td className="py-2 px-3 hidden md:table-cell max-w-[200px] text-muted-foreground">
                    {missingDisplay ? <span className="text-foreground">{missingDisplay}</span> : "—"}
                  </td>
                )}
                {showNotes && (
                  <td className="py-2 px-3 hidden lg:table-cell max-w-[200px] truncate text-muted-foreground">{notesDisplay || "—"}</td>
                )}
                <td className="py-2 px-3 hidden sm:table-cell"><QuietBadge value={job.currentStage} /></td>
                <td className="py-2 px-3">
                  <Link to={`/jobs/${job.id}`}><ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60 hover:text-foreground transition-colors" /></Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ── Tomorrow's Prep Checklist ── */
function TomorrowPrepChecklist({
  serverJobs,
  serverPagination,
}: {
  serverJobs?: WarehouseJob[];
  serverPagination?: PaginationControls;
}) {
  const { jobs } = useAppData();
  const tomorrow = addDays(new Date(), 1);
  const [checked, setChecked] = useState<Record<string, Record<string, boolean>>>({});

  const tomorrowJobs = useMemo(() => {
    if (serverJobs) return serverJobs;
    return jobs.filter((j) => {
      if (j.currentStage === "Completed") return false;
      try {
        const d = parseISO(j.scheduledInstallDate || j.targetInstallDate);
        return isSameDay(d, tomorrow);
      } catch { return false; }
    });
  }, [jobs, serverJobs, tomorrow]);
  const prepPage = usePagination(tomorrowJobs, 15);
  const prepRows = serverPagination ? tomorrowJobs : prepPage.paged;

  useEffect(() => {
    prepPage.reset();
  }, [tomorrowJobs.length]);

  // Seed checked state from persisted DB metadata when server jobs load
  useEffect(() => {
    if (!serverJobs || serverJobs.length === 0) return;
    setChecked((prev) => {
      const next = { ...prev };
      for (const job of serverJobs) {
        if (next[job.id]) continue; // don't overwrite in-session changes
        const meta = (job as WarehouseHubJobRow).metadata;
        const saved = meta?.prepChecklist as Record<string, boolean> | undefined;
        if (saved && typeof saved === "object") {
          next[job.id] = { ...saved };
        }
      }
      return next;
    });
  }, [serverJobs]);

  const toggle = (jobId: string, key: string) => {
    const updatedJob = { ...checked[jobId], [key]: !checked[jobId]?.[key] };
    setChecked((prev) => ({ ...prev, [jobId]: updatedJob }));
    void WarehousePageService.saveJobPrepChecklist(jobId, updatedJob);
  };

  const isChecked = (jobId: string, key: string) => !!checked[jobId]?.[key];

  const completedCount = tomorrowJobs.filter((j) => {
    const c = checked[j.id];
    const hasMissing = ["Missing Items", "Field Purchase Required"].includes(getEffectiveReadiness(j));
    return c?.staged && c?.loaded && (!hasMissing || c?.missing);
  }).length;

  const missingCount = tomorrowJobs.filter((j) => ["Missing Items", "Field Purchase Required"].includes(getEffectiveReadiness(j))).length;
  const notStaged = tomorrowJobs.filter((j) => !isWarehouseReady(j)).length;
  const unassigned = tomorrowJobs.filter((j) => !j.assignedCrew && !j.assignedSubcontractor).length;
  const th = "text-left py-2 px-3 font-medium text-muted-foreground/80 uppercase text-[10px] tracking-[0.08em]";

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="space-y-1">
          <h2 className="text-sm font-medium tracking-tight text-foreground">
            Tomorrow's prep
            <span className="ml-2 text-muted-foreground font-normal">{format(tomorrow, "EEE, MMM d")}</span>
          </h2>
          <p className="text-[11px] text-muted-foreground">Stage, load, and reconcile missing items before dispatch.</p>
        </div>
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {completedCount}/{tomorrowJobs.length} ready
        </span>
      </div>

      {tomorrowJobs.length > 0 && (
        <div className="w-full bg-muted/60 rounded-full h-[3px] overflow-hidden">
          <div
            className="bg-foreground/70 h-full transition-all"
            style={{ width: `${tomorrowJobs.length ? (completedCount / tomorrowJobs.length) * 100 : 0}%` }}
          />
        </div>
      )}

      {tomorrowJobs.length > 0 && (missingCount > 0 || notStaged > 0 || unassigned > 0) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground tabular-nums">
          {missingCount > 0 && <span><span className="text-destructive font-medium">{missingCount}</span> missing materials</span>}
          {notStaged > 0 && <span><span className="text-foreground font-medium">{notStaged}</span> not staged</span>}
          {unassigned > 0 && <span><span className="text-foreground font-medium">{unassigned}</span> unassigned</span>}
        </div>
      )}

      {tomorrowJobs.length === 0 ? (
        <div className="py-12 text-center text-xs text-muted-foreground">
          No jobs scheduled for tomorrow.
        </div>
      ) : (
        <div className="overflow-auto border-y border-border/60">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/60">
                <th className={th}>Job</th>
                <th className={th}>Customer</th>
                <th className={cn(th, "hidden sm:table-cell")}>Crew</th>
                <th className={th}>Materials</th>
                <th className={cn(th, "hidden md:table-cell")}>Missing</th>
                <th className={cn(th, "text-center")}>Staged</th>
                <th className={cn(th, "text-center")}>Loaded</th>
                <th className={cn(th, "text-center hidden sm:table-cell")}>Missing OK</th>
                <th className="py-2 px-3 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {prepRows.map((job) => {
                const readiness = getEffectiveReadiness(job);
                const hasMissing = readiness === "Missing Items" || readiness === "Field Purchase Required";
                const missingDisplay = getMissingItemsDisplay(job);
                const allDone = isChecked(job.id, "staged") && isChecked(job.id, "loaded") && (!hasMissing || isChecked(job.id, "missing"));
                return (
                  <tr key={job.id} className={cn("transition-colors", allDone ? "bg-muted/20" : "hover:bg-muted/30")}>
                    <td className="py-2 px-3 font-mono text-[11px] text-foreground/90">
                      <Link to={`/jobs/${job.id}`} className="hover:text-foreground hover:underline">{getJobDisplayRef(job)}</Link>
                    </td>
                    <td className="py-2 px-3 text-foreground">{job.customerName}</td>
                    <td className="py-2 px-3 hidden sm:table-cell text-foreground/80">
                      {job.assignedCrew || job.assignedSubcontractor || <span className="text-muted-foreground">Unassigned</span>}
                    </td>
                    <td className="py-2 px-3"><QuietBadge value={readiness} /></td>
                    <td className="py-2 px-3 hidden md:table-cell max-w-[180px] text-muted-foreground">
                      {hasMissing
                        ? <span className="text-foreground">{missingDisplay || "Review material list"}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 px-3 text-center"><Checkbox checked={isChecked(job.id, "staged")} onCheckedChange={() => toggle(job.id, "staged")} /></td>
                    <td className="py-2 px-3 text-center"><Checkbox checked={isChecked(job.id, "loaded")} onCheckedChange={() => toggle(job.id, "loaded")} /></td>
                    <td className="py-2 px-3 text-center hidden sm:table-cell">
                      {hasMissing
                        ? <Checkbox checked={isChecked(job.id, "missing")} onCheckedChange={() => toggle(job.id, "missing")} />
                        : <span className="text-muted-foreground/60">—</span>}
                    </td>
                    <td className="py-2 px-3">{allDone && <CheckCircle className="h-3.5 w-3.5 text-foreground/70" />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <PaginationBar {...(serverPagination ?? prepPage)} />
        </div>
      )}
    </section>
  );
}
